import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { FormDialog, FormTextField, ResourceState, ValueBadge } from '../../../design-system';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { registerNavigationGuard } from '../../../shared/navigationGuard';
import type { EmployeeDirectoryPort } from '../../people/public';
import { WorkflowPersonPicker } from '../WorkflowReferencePickers';
import { workflowPersonOptions } from '../models/workflowRequestPageModel';
import { fixtureWorkflowEmployeeDirectory } from '../fixtures/workflowPageFixtures';
import { isAmbiguousApiMutationFailure } from '../workflowPageSupport';
import { shiftWorkflowDate, workflowDateInSeoul, workflowDelegationRow, workflowProcessOptions, type WorkflowDelegation } from '../operations/model';
import type { WorkflowSelfDelegationInput, WorkflowSelfDelegationPort } from './port';

const statusLabels: Record<string, string> = { ACTIVE: '위임 중', SCHEDULED: '예정', EXPIRED: '기간 종료', REVOKED: '회수됨' };
type Props = {
  port: WorkflowSelfDelegationPort;
  directoryPort: EmployeeDirectoryPort;
  currentPersonId: string;
  fixtureMode: boolean;
  canSearchPeople: boolean;
  disabled?: boolean;
  onChanged?: () => void;
};

export function WorkflowSelfDelegationAction({ port, directoryPort, currentPersonId, fixtureMode, canSearchPeople, disabled, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [delegateId, setDelegateId] = useState('');
  const [processType, setProcessType] = useState('');
  const [startsOn, setStartsOn] = useState(workflowDateInSeoul);
  const [endsOn, setEndsOn] = useState(() => shiftWorkflowDate(workflowDateInSeoul(), 7));
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<string>();
  const [fixtureItems, setFixtureItems] = useState<WorkflowDelegation[]>([]);
  const attempt = useRef<{ input: WorkflowSelfDelegationInput; key: string } | undefined>(undefined);
  const pendingRef = useRef(false);
  const dirty = creating && Boolean(delegateId || reason || processType);
  const items = useAsyncResource(() => !open ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureItems) : port.list(), [port, open, fixtureMode, fixtureItems]);
  const directory = useAsyncResource(() => !creating || !canSearchPeople ? Promise.resolve(undefined) : fixtureMode ? Promise.resolve(fixtureWorkflowEmployeeDirectory) : directoryPort.loadDirectory(), [directoryPort, creating, canSearchPeople, fixtureMode]);
  const people = useMemo(() => workflowPersonOptions(directory.data).filter(person => person.personId !== currentPersonId), [directory.data, currentPersonId]);
  const peopleError = canSearchPeople ? directory.error?.message : '구성원 검색 권한이 필요합니다. 관리자에게 문의해 주세요.';
  useEffect(() => {
    if (!open) return;
    const guard = () => !pendingRef.current && (!dirty || window.confirm('작성 중인 위임 내용을 버리고 이동할까요?'));
    const unregister = registerNavigationGuard(guard);
    const beforeUnload = (event: BeforeUnloadEvent) => { if (pendingRef.current || dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);
    return () => { unregister(); window.removeEventListener('beforeunload', beforeUnload); };
  }, [open, dirty]);
  const reset = () => { setCreating(false); setDelegateId(''); setProcessType(''); setReason(''); setError(''); setUncertain(false); attempt.current = undefined; };
  const close = () => {
    if (pendingRef.current) return;
    if (uncertain) { setOpen(false); return; }
    if (dirty && !window.confirm('작성 중인 위임 내용을 버릴까요?')) return;
    if (creating) reset(); else { setOpen(false); setRevokeTarget(undefined); }
  };
  const create = async () => {
    if (pendingRef.current || disabled) return;
    if (!creating) { setNotice(''); setError(''); setCreating(true); return; }
    if (!delegateId || !startsOn || !endsOn) { setError('수임자와 위임 기간을 입력해 주세요.'); return; }
    if (!shiftWorkflowDate(startsOn, 0) || !shiftWorkflowDate(endsOn, 0) || endsOn < startsOn) { setError('종료일은 시작일과 같거나 이후로 선택해 주세요.'); return; }
    const next = attempt.current ?? { input: { delegateId, processType, startsOn, endsOn: shiftWorkflowDate(endsOn, 1), reason }, key: crypto.randomUUID() };
    pendingRef.current = true; setPending(true); setError('');
    try {
      if (fixtureMode) setFixtureItems(values => [...values, { id: crypto.randomUUID(), ...next.input, delegatorId: currentPersonId, delegateDescriptor: people.find(person => person.personId === delegateId)?.name, createdAt: new Date().toISOString() }]);
      else await port.create(next.input, next.key);
      reset(); setNotice('부재중 위임을 등록했습니다.'); items.reload(); onChanged?.();
    } catch (cause) {
      if (isAmbiguousApiMutationFailure(cause)) { attempt.current = next; setUncertain(true); }
      setError(cause instanceof Error ? cause.message : '위임을 등록하지 못했습니다.');
    } finally { pendingRef.current = false; setPending(false); }
  };
  const revoke = async (id: string) => {
    if (pendingRef.current || disabled) return;
    pendingRef.current = true; setPending(true); setError('');
    try {
      if (fixtureMode) setFixtureItems(values => values.map(value => value.id === id ? { ...value, revokedAt: new Date().toISOString() } : value));
      else await port.revoke(id);
      setRevokeTarget(undefined); setNotice('위임을 회수했습니다.'); items.reload(); onChanged?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : '위임을 회수하지 못했습니다.'); }
    finally { pendingRef.current = false; setPending(false); }
  };
  return <>
    <Button variant="outlined" disabled={disabled} onClick={() => { setOpen(true); setNotice(''); }}>부재중 위임</Button>
    <FormDialog open={open} onClose={close} onSubmit={create} title={creating ? '부재중 위임 등록' : '내 부재중 위임'}
      help={creating
        ? '개인에게 배정된 결재를 위임합니다. 역할에 배정된 결재와 권한은 위임되지 않으며, 수임자에게 조회·결재 권한이 필요합니다. 기간은 한국 시간 기준이며 종료일을 포함합니다.'
        : '개인에게 배정된 결재를 대신 처리할 구성원과 기간을 관리합니다. 예정된 위임과 진행 중인 위임은 언제든 회수할 수 있습니다.'}
      cancelLabel={creating ? '이전' : '닫기'} submitLabel={creating ? uncertain ? '같은 내용으로 다시 시도' : '위임 등록' : '새 위임 등록'} busy={pending} busyLabel="처리 중…"
      submitDisabled={Boolean(disabled || (creating && (!canSearchPeople || directory.loading)))}>
      {notice ? <Alert severity="success">{notice}</Alert> : null}
      {creating ? <>
        <WorkflowPersonPicker label="수임자" personId={delegateId} options={people} onChange={setDelegateId} disabled={pending || uncertain || !canSearchPeople} loading={directory.loading} error={peopleError} onRetry={directory.reload} />
        <FormTextField select label="적용 업무" value={processType} onChange={event => setProcessType(event.target.value)} disabled={pending || uncertain}>
          <MenuItem value="">모든 업무</MenuItem>{workflowProcessOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </FormTextField>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <FormTextField required type="date" label="시작일" value={startsOn} onChange={event => setStartsOn(event.target.value)} disabled={pending || uncertain} />
          <FormTextField required type="date" label="종료일" value={endsOn} onChange={event => setEndsOn(event.target.value)} helperText="이 날 23:59까지 위임합니다." disabled={pending || uncertain} />
        </Stack>
        <FormTextField label="사유" value={reason} onChange={event => setReason(event.target.value)} multiline minRows={2} disabled={pending || uncertain} />
        {uncertain ? <Alert severity="warning">등록 결과를 확인하지 못했습니다. 같은 내용으로 다시 시도하면 중복 등록을 방지할 수 있습니다.</Alert> : null}
      </> : <>
        <ResourceState embedded loading={items.loading} error={items.error?.message} onRetry={items.reload} empty={!items.data?.length} emptyTitle="등록한 부재중 위임이 없습니다." emptyAction={false}>
          <Stack spacing={2} divider={<Divider />}>{(items.data ?? []).map(item => { const row = workflowDelegationRow(item); const revocable = ['ACTIVE', 'SCHEDULED'].includes(row.status); return <Stack key={item.id} spacing={1}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}><Typography sx={{ fontWeight: 600 }}>{item.delegateDescriptor || '지정한 구성원'}</Typography><ValueBadge label={statusLabels[row.status]} tone={row.status === 'ACTIVE' ? 'accent' : 'neutral'} /></Stack>
            <Typography variant="body2" color="text.secondary">{row.descriptor} · {row.period}</Typography>
            {revokeTarget === item.id ? <Alert severity="warning" action={<Button color="error" disabled={pending} onClick={() => void revoke(item.id)}>회수 확정</Button>}>이 위임을 회수할까요? <Button disabled={pending} onClick={() => setRevokeTarget(undefined)}>유지</Button></Alert>
              : revocable ? <Button color="error" sx={{ alignSelf: 'flex-start' }} disabled={pending} onClick={() => { setRevokeTarget(item.id); setError(''); }}>위임 회수</Button> : null}
          </Stack>; })}</Stack>
        </ResourceState>
      </>}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </FormDialog>
  </>;
}
